/**
 * Crypto utilities for DEX sealed box encryption.
 * Uses libsodium for NaCl sealed box (X25519 + XSalsa20-Poly1305).
 */

import sodium from 'libsodium-wrappers';

// Track whether sodium has been initialized
let sodiumReady = false;

/**
 * Initialize the sodium library.
 * Must be called before any crypto operations.
 * Safe to call multiple times - will only initialize once.
 * @returns {Promise<void>}
 */
export async function initCrypto() {
  if (sodiumReady) {
    return;
  }
  await sodium.ready;
  sodiumReady = true;
}

/**
 * Encrypt content for a specific reader using sealed box encryption.
 * The reader can decrypt with their private key; the sender's identity is anonymous.
 *
 * @param {string|Uint8Array|ArrayBuffer} content - Content to encrypt
 * @param {string} publicKeyBase64 - Reader's public key (base64 encoded)
 * @returns {Promise<string>} Encrypted content as base64
 */
export async function encryptForReader(content, publicKeyBase64) {
  // Ensure sodium is initialized
  await initCrypto();

  // Decode the public key from base64 (ORIGINAL variant for PyNaCl compatibility)
  const publicKey = sodium.from_base64(publicKeyBase64, sodium.base64_variants.ORIGINAL);

  // Convert content to bytes
  let contentBytes;
  if (typeof content === 'string') {
    contentBytes = sodium.from_string(content);
  } else if (content instanceof ArrayBuffer) {
    contentBytes = new Uint8Array(content);
  } else {
    contentBytes = new Uint8Array(content);
  }

  // Encrypt using sealed box
  const ciphertext = sodium.crypto_box_seal(contentBytes, publicKey);

  // Return as base64 (ORIGINAL variant for PyNaCl compatibility)
  return sodium.to_base64(ciphertext, sodium.base64_variants.ORIGINAL);
}

/**
 * Estimate the size of encrypted content in base64.
 * Useful for showing users expected payload size before encryption.
 *
 * @param {number} rawSize - Size of raw content in bytes
 * @returns {number} Estimated size of encrypted base64 string in characters
 */
export function estimateEncryptedSize(rawSize) {
  // crypto_box_SEALBYTES = 48 bytes overhead
  // Base64 encoding: 4 characters per 3 bytes
  const encryptedBytes = rawSize + 48;
  return Math.ceil(encryptedBytes * 4 / 3);
}

/**
 * Decrypt content encrypted with sealed box encryption.
 * The sender's identity is anonymous; only the reader's private key can decrypt.
 * The public key is derived from the private key automatically.
 *
 * @param {string} ciphertextBase64 - Encrypted content (base64 encoded)
 * @param {string} privateKeyBase64 - Reader's private key (base64 encoded)
 * @returns {Promise<Uint8Array>} Decrypted plaintext as bytes
 * @throws {Error} If decryption fails (invalid key, corrupted data, etc.)
 */
export async function decryptPayload(ciphertextBase64, privateKeyBase64) {
  // Ensure sodium is initialized
  await initCrypto();

  // Decode inputs from base64 (ORIGINAL variant for PyNaCl compatibility)
  const ciphertext = sodium.from_base64(ciphertextBase64, sodium.base64_variants.ORIGINAL);
  const privateKey = sodium.from_base64(privateKeyBase64, sodium.base64_variants.ORIGINAL);

  // Derive the public key from the private key — no need for the user to provide it
  const publicKey = sodium.crypto_scalarmult_base(privateKey);

  try {
    // Decrypt using sealed box
    const plaintext = sodium.crypto_box_seal_open(ciphertext, publicKey, privateKey);

    return plaintext;
  } catch (error) {
    throw new Error(`Decryption failed: ${error.message || 'Invalid key or corrupted data'}`);
  }
}

/**
 * Generate a new X25519 keypair for sealed box encryption.
 *
 * @returns {Promise<{publicKey: string, privateKey: string, keyType: string}>}
 *   Object with base64-encoded keys and key type identifier
 */
export async function generateKeypair() {
  // Ensure sodium is initialized
  await initCrypto();

  // Generate keypair
  const keypair = sodium.crypto_box_keypair();

  // Return as base64 (ORIGINAL variant for PyNaCl compatibility)
  return {
    publicKey: sodium.to_base64(keypair.publicKey, sodium.base64_variants.ORIGINAL),
    privateKey: sodium.to_base64(keypair.privateKey, sodium.base64_variants.ORIGINAL),
    keyType: 'X25519'
  };
}

/**
 * Validate a private key string for format and length.
 *
 * @param {string} keyString - Private key to validate
 * @returns {{valid: boolean, key?: string, error?: string}}
 *   Validation result with cleaned key on success or error message on failure
 */
export function validatePrivateKey(keyString) {
  // Check for falsy input
  if (!keyString) {
    return { valid: false, error: 'Private key is required' };
  }

  // Trim whitespace
  const cleaned = keyString.trim();

  // Check base64 format (standard base64 alphabet with optional padding)
  const base64Regex = /^[A-Za-z0-9+/]+=*$/;
  if (!base64Regex.test(cleaned)) {
    return { valid: false, error: 'Invalid base64 format' };
  }

  // Check length: 32 bytes = 44 characters in base64 (with padding)
  if (cleaned.length !== 44) {
    return { valid: false, error: 'Invalid key length (expected 32 bytes / 44 characters)' };
  }

  return { valid: true, key: cleaned };
}
